import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { finalize } from 'rxjs/operators';
import { ApiService } from 'src/app/core/providers/api/api.service';
import { FormDataApiService } from 'src/app/core/providers/apiFormData/FormDataApiService';
import { Comment, CommentDraft, Document } from './comment.models';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';
import { FileSaverService } from 'ngx-filesaver';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NgSelectModule } from '@ng-select/ng-select';
import { FilePreviewModalComponent } from 'src/app/shared/file-preview-modal/file-preview-modal.component';
import { MentionTextareaComponent } from 'src/app/shared/mention-textarea/mention-textarea.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthenticationService } from 'src/app/core/providers/auth/auth.service';
import { confirmService } from 'src/app/core/providers/confirm/confirmService';

@Component({
  selector: 'app-comment-field',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NgSelectModule,
    MentionTextareaComponent,
    TranslateModule,
  ],
  templateUrl: './comment-field.component.html',
  styleUrls: ['./comment-field.component.scss'],
})
export class CommentFieldComponent implements OnInit {
  @Input() ownerId!: string;
  @Input() ownerClass!: string;
  @Input() ownerCommentClass!: string;
  @Input() defaultExpanded: boolean = false; // Permet de définir si les commentaires sont dépliés par défaut
  @Input() draftMode: boolean = false;
  @Input() hideHistory: boolean = false;
  @Output() commentAdded = new EventEmitter<void>();
  @Output() commentDeleted = new EventEmitter<{ hasComments: boolean }>();
  @Output() draftChange = new EventEmitter<CommentDraft>();

  //@ViewChild('commentTextarea') textarea!: ElementRef<HTMLDivElement>;
  @ViewChild('mentionTextarea') mentionTextarea!: MentionTextareaComponent;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;

  newComment: string = '';
  selectedFiles: File[] = [];
  selectedTags: any[] = [];
  availableTags: any[] = [];
  tagsLoading: boolean = false;

  comments: Comment[] = [];
  isLoading: boolean = true;
  isPosting: boolean = false;
  isDeleting: { [key: string]: boolean } = {}; // État de suppression par commentaire
  error: string | null = null;
  currentPage: number = 1;
  itemsPerPage: number = 10;
  hasMoreComments: boolean = true;
  mentions: any[] = []; // Stocker les mentions
  isCommentsExpanded: boolean = false; // État d'expansion des commentaires

  constructor(
    private http: HttpClient,
    private fileSaver: FileSaverService,
    private sanitizer: DomSanitizer,
    private apiService: ApiService,
    private formDataApiService: FormDataApiService,
    private modalService: NgbModal,
    private authService: AuthenticationService<any>,
    private confirmService: confirmService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    // Initialiser l'état d'expansion avec la valeur par défaut
    this.isCommentsExpanded = this.defaultExpanded;
    if (!this.draftMode && !this.hideHistory) {
      this.loadComments();
    } else {
      this.isLoading = false;
    }
    this.loadAvailableTags();
  }

  loadAvailableTags(searchTerm?: string): void {
    this.tagsLoading = true;
    const params: any = { ownerClass: 'COMMENT_' + this.ownerClass };
    if (searchTerm) {
      params.name = searchTerm;
    }

    this.apiService
      .getAll('tags', 1, 10, params)
      .pipe(finalize(() => (this.tagsLoading = false)))
      .subscribe({
        next: (response: any) => {
          this.availableTags = (response.member || []).map((tag: any) => ({
            id: tag.id,
            name: tag.name,
            type: tag.type,
          }));
        },
        error: (err) => {
          console.error('Erreur lors du chargement des tags', err);
        },
      });
  }

  onTagSearch(term: any): void {
    this.loadAvailableTags(term);
  }

  onTagAdd(event: any): void {
    // Si c'est un nouveau tag (ajouté via la saisie)
    if (event && typeof event === 'string') {
      this.selectedTags.push({
        id: null,
        name: event,
        type: 'TAG',
      });
    }
    this.emitDraft();
  }

  loadComments(loadMore: boolean = false): void {
    if (loadMore) {
      this.currentPage++;
    } else {
      this.currentPage = 1;
      this.comments = [];
    }

    this.isLoading = true;
    this.error = null;

    this.apiService
      .getAll(
        'comments',
        this.currentPage,
        this.itemsPerPage,
        {
          ownerId: this.ownerId,
          ownerClass: this.ownerClass,
          'order[createdAt]': 'DESC',
        },
        ['author'],
      ) // Charger la relation author
      .pipe(finalize(() => (this.isLoading = false)))
      .subscribe({
        next: (response: any) => {
          const newComments = response.member || [];
          this.hasMoreComments = newComments.length === this.itemsPerPage;

          this.comments = loadMore
            ? [...this.comments, ...this.processComments(newComments)]
            : this.processComments(newComments);
        },
        error: (err) => {
          this.error = 'Erreur lors du chargement des commentaires';
          console.error(err);
        },
      });
  }

  private processComments(comments: any[]): Comment[] {
    const currentUser = this.authService.currentUserValue;

    return comments.map((comment) => {
      // Extraire l'ID de l'auteur depuis différentes structures possibles
      let authorId: string | undefined;

      // Cas 1: authorId direct
      if (comment.authorId) {
        authorId = comment.authorId;
      }
      // Cas 2: createdBy
      else if (comment.createdBy) {
        authorId = comment.createdBy;
      }
      // Cas 3: author est un objet avec id
      else if (comment.author?.id) {
        authorId = comment.author.id;
      }
      // Cas 4: author est une relation API Platform avec @id
      else if (comment.author?.['@id']) {
        // Extraire l'ID depuis l'URI (ex: /api/users/123 -> 123)
        const authorUri = comment.author['@id'];
        const matches = authorUri.match(/\/([^\/]+)$/);
        authorId = matches ? matches[1] : undefined;
      }
      // Cas 5: author est une string (URI)
      else if (typeof comment.author === 'string') {
        const matches = comment.author.match(/\/([^\/]+)$/);
        authorId = matches ? matches[1] : undefined;
      }
      // Cas 6: Si l'utilisateur actuel correspond par nom/prénom, utiliser son ID
      else if (
        currentUser &&
        comment.authorFirstName &&
        comment.authorLastName
      ) {
        if (
          currentUser.firstName === comment.authorFirstName &&
          currentUser.lastName === comment.authorLastName
        ) {
          authorId = currentUser.id;
        }
      }

      return {
        id: comment.id,
        ownerClass: comment.ownerClass,
        ownerId: comment.ownerId,
        content: comment.content,
        createdAt: new Date(comment.createdAt),
        updatedAt: new Date(comment.updatedAt),
        author: {
          id: authorId,
          firstName: comment.authorFirstName || comment.author?.firstName,
          lastName: comment.authorLastName || comment.author?.lastName,
        },
        documents: comment.documents?.map((doc: any) => ({
          id: doc.id,
          name: doc.name,
          contentUrl: doc.contentUrl,
          documentType: doc.documentType,
          isImage: doc.documentType?.startsWith('image/'),
        })),
        tags: comment.tags,
        timeAgo: this.getTimeAgo(new Date(comment.createdAt)),
        authorInitial: this.getInitial(
          comment.authorFirstName || comment.author?.firstName,
          comment.authorLastName || comment.author?.lastName,
        ),
      };
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files && input.files.length > 0) {
      this.selectedFiles = Array.from(input.files);
      this.emitDraft();
    }
  }

  removeFile(index: number): void {
    this.selectedFiles.splice(index, 1);
    this.emitDraft();
  }

  // Méthode pour gérer les mentions
  handleMentions(mentions: any[]): void {
    this.mentions = mentions;
  }

  onContentChange(content: string): void {
    this.newComment = content;
    this.emitDraft();
  }

  onTagsChange(): void {
    this.emitDraft();
  }

  postComment(): void {
    if (!this.newComment.trim()) return;

    this.isPosting = true;

    // Construction du payload de base
    const payload: any = {
      content: this.newComment,
      ownerClass: this.ownerClass,
      ownerId: this.ownerId,
      type: this.ownerCommentClass,
    };

    // Ajout des tags au format attendu
    this.selectedTags.forEach((tag, i) => {
      payload[`tags[${i}][id]`] = tag.id;
      payload[`tags[${i}][name]`] = tag.name;
      payload[`tags[${i}][type]`] = tag.type || 'TAG';
    });

    // Ajout des fichiers sélectionnés
    this.selectedFiles.forEach((file, i) => {
      payload[`documents[${i}][file]`] = file;
      payload[`documents[${i}][name]`] = file.name;
      payload[`documents[${i}][type]`] = 'COMMENT_ATTACHMENT';
    });

    // Appel à l'API
    this.formDataApiService
      .create('comments', payload)
      .pipe(finalize(() => (this.isPosting = false)))
      .subscribe({
        next: () => {
          this.resetForm();
          this.loadComments();
          // Notifier les parents qu'un commentaire a été ajouté
          this.commentAdded.emit();
        },
        error: (err) => {
          this.error = "Erreur lors de l'envoi du commentaire";
          console.error(err);
        },
      });
  }

  previewFile(doc: any): void {
    const modalRef = this.modalService.open(FilePreviewModalComponent, {
      size: 'xl',
      centered: true,
    });
    modalRef.componentInstance.fileUrl = this.getFullUrl(doc.contentUrl);
    modalRef.componentInstance.fileName = doc.name;
    modalRef.componentInstance.fileType = doc.documentType;
  }

  public getFullUrl(relativeUrl: string): string {
    return `${environment.apiUrl}${relativeUrl}`;
  }

  private resetForm(): void {
    this.newComment = '';
    this.selectedFiles = [];
    this.selectedTags = [];
    this.mentionTextarea.clear();
    if (this.fileInput.nativeElement) {
      this.fileInput.nativeElement.value = '';
    }
  }

  private emitDraft(): void {
    if (!this.draftMode) {
      return;
    }

    this.draftChange.emit({
      content: this.newComment,
      tags: this.selectedTags,
      files: this.selectedFiles,
    });
  }

  download(doc: any) {
    const mediaPrefix = `/media/`;
    const relativePath = doc.contentUrl.replace(mediaPrefix, '');
    const encodedPath = encodeURIComponent(relativePath);
    const downloadUrl = `${environment.apiUrl}/api/secure-download?path=${encodedPath}`;

    this.http.get(downloadUrl, { responseType: 'blob' }).subscribe({
      next: (blob) => {
        const extension = doc.contentUrl.split('.').pop();
        const filename = `${doc.name}.${extension}`;
        this.fileSaver.save(blob, filename);
      },
      error: (err) => {
        console.error('Download failed:', err);
      },
    });
  }

  // Méthodes utilitaires
  getInitial(firstName: string, lastName: string): string {
    return `${firstName?.[0] || ''}${lastName?.[0] || ''}`.toUpperCase();
  }

  getTimeAgo(date: Date): string {
    const seconds = Math.floor((new Date().getTime() - date.getTime()) / 1000);

    if (seconds < 60) return "À l'instant";
    if (seconds < 120) return 'Il y a 1 minute';
    if (seconds < 3600) return `Il y a ${Math.floor(seconds / 60)} minutes`;
    if (seconds < 7200) return 'Il y a 1 heure';
    if (seconds < 86400) return `Il y a ${Math.floor(seconds / 3600)} heures`;

    const days = Math.floor(seconds / 86400);
    if (days === 1) return 'Hier';
    if (days < 7) return `Il y a ${days} jours`;

    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long' });
  }

  sanitizeHtml(html: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(html);
  }

  toggleCommentsExpansion(): void {
    this.isCommentsExpanded = !this.isCommentsExpanded;
  }

  // Vérifie si l'utilisateur actuel est l'auteur du commentaire
  isCommentAuthor(comment: Comment): boolean {
    const currentUser = this.authService.currentUserValue;
    if (!currentUser) {
      return false;
    }

    // Si on a l'ID de l'auteur, comparer directement
    if (comment.author?.id) {
      return currentUser.id === comment.author.id;
    }

    // Sinon, comparer par nom et prénom (fallback)
    // C'est utile quand l'API ne retourne pas l'ID de l'auteur
    if (comment.author?.firstName && comment.author?.lastName) {
      const isMatch =
        currentUser.firstName === comment.author.firstName &&
        currentUser.lastName === comment.author.lastName;

      // Si c'est un match, on peut aussi mettre à jour l'ID pour éviter de refaire la comparaison
      if (isMatch && !comment.author.id) {
        comment.author.id = currentUser.id;
      }

      return isMatch;
    }

    return false;
  }

  // Supprime un commentaire avec confirmation
  deleteComment(comment: Comment): void {
    if (!comment.id) return;

    // Afficher la modal de confirmation
    this.confirmService
      .openConfirmationModal(
        this.translate.instant('commentField.deleteTitle'),
        this.translate.instant('commentField.deleteMessage'),
        this.translate.instant('commentField.confirmDelete'),
        this.translate.instant('commentField.cancel'),
      )
      .then((confirmed) => {
        if (confirmed) {
          this.performDelete(comment);
        }
      })
      .catch(() => {
        // L'utilisateur a annulé ou fermé la modal
      });
  }

  // Effectue la suppression du commentaire
  private performDelete(comment: Comment): void {
    if (!comment.id) return;

    this.isDeleting[comment.id] = true;

    this.apiService.delete('comments', comment.id).subscribe({
      next: () => {
        // Retirer le commentaire de la liste
        this.comments = this.comments.filter((c) => c.id !== comment.id);
        delete this.isDeleting[comment.id];

        // Vérifier s'il reste des commentaires
        const hasComments = this.comments.length > 0;

        // Notifier les parents qu'un commentaire a été supprimé avec l'état actuel
        this.commentDeleted.emit({ hasComments });
        // Également émettre commentAdded pour la compatibilité avec l'ancien code
        this.commentAdded.emit();
      },
      error: (err) => {
        this.error = this.translate.instant('commentField.deleteError');
        console.error(err);
        delete this.isDeleting[comment.id];
      },
    });
  }
}
